import {
  IStorageClientOptions,
  IData_Storage_Breadcrumb,
  IStorage_Filenames,
  IField_Storage_File,
  IField_Storage_Pointer,
  IField_Filestore_Type,
  IField_Storage_Type,
  IField_Storage_Access_Type,
} from '../types/storage'

import {
  STORAGE_BUCKET_PREFIX,
  STORAGE_LOCATIONS,
  STORAGE_TYPE_PUBLIC,
  STORAGE_BUCKET_PUBLIC_URL,
  STORAGE_BUCKET_NAMES,
  FILESTORE_STORAGE_TYPES,
  FILESTORE_TYPE_SHARED,
  FILESTORE_TYPE_PUBLIC,
  FILESTORE_ACCESS_TYPES,
  FILESTORE_ACCESS_TYPE_SCORES,
  STORAGE_ACCESS_TYPE_PUBLIC,
  IStorageLocationName,
} from '../constants'

import {
  getPrefix,
  constructUrl,
  getFilestoreMediaUrl,
  getFilestoreDownloadUrl,
} from './url'

import {
  isImageExtension,
  getFileExtension,
  isFolder,
} from './file'

export const getPathBreadcrumbs = (path: string): IData_Storage_Breadcrumb[] => {
  const results: IData_Storage_Breadcrumb[] = []
  const allParts: string[] = []
  const parts = (path || '').split('/').filter(p => p)

  parts.forEach(part => {
    allParts.push(part)
    results.push({
      name: part,
      path: allParts.join('/') + '/',
    })
  })

  return results
}

export const getAccessTypeScore = (accessType: IField_Storage_Access_Type) => FILESTORE_ACCESS_TYPE_SCORES[accessType]
export const getFilestoreTypeStorageType = (filestoreType: IField_Filestore_Type): IField_Storage_Type => FILESTORE_STORAGE_TYPES[filestoreType] || STORAGE_TYPE_PUBLIC
export const getFilestoreTypeAccessType = (filestoreType: IField_Filestore_Type): IField_Storage_Access_Type => FILESTORE_ACCESS_TYPES[filestoreType] || STORAGE_ACCESS_TYPE_PUBLIC
export const isFilestoreTypePublic = (filestoreType: IField_Filestore_Type): boolean => getFilestoreTypeStorageType(filestoreType) == STORAGE_TYPE_PUBLIC
export const getPointerStorageType = (pointer: IField_Storage_Pointer): IField_Storage_Type => pointer && pointer.type ? FILESTORE_STORAGE_TYPES[pointer.type] : STORAGE_TYPE_PUBLIC
export const getPointerFilestoreType = (pointer: IField_Storage_Pointer): IField_Filestore_Type => pointer && pointer.type ? pointer.type : FILESTORE_TYPE_PUBLIC
export const getPointerAccessType = (pointer: IField_Storage_Pointer): IField_Storage_Access_Type => FILESTORE_ACCESS_TYPES[getPointerFilestoreType(pointer)] || STORAGE_ACCESS_TYPE_PUBLIC
export const getPointerBucketName = (pointer: IField_Storage_Pointer): string => STORAGE_BUCKET_NAMES[getPointerStorageType(pointer)]
export const getPointerFilenames = (pointer: IField_Storage_Pointer): IStorage_Filenames => StorageFilenames({
  type: getPointerStorageType(pointer),
})
export const constructPath = (parts: string[]): string => parts.filter(p => p).join('/').replace(/\/+/g, '/')
export const getPointerFolderParts = (pointer: IField_Storage_Pointer): string[] => {
  return [
    'files',
    pointer.project,
    pointer.type == FILESTORE_TYPE_SHARED ? null : pointer.org,
    pointer.type,
  ].filter(p => p)
}
export const getPointerPathParts = (pointer: IField_Storage_Pointer): string[] => {
  if(!pointer) return []
  return [
    'files',
    pointer.project,
    pointer.type == FILESTORE_TYPE_SHARED ? null : pointer.org,
    pointer.type,
    pointer.path,
  ].filter(p => p)
}
export const getPointerFolder = (pointer: IField_Storage_Pointer): string => constructPath(getPointerFolderParts(pointer))
export const getPointerPath = (pointer: IField_Storage_Pointer): string => constructPath(getPointerPathParts(pointer))
export const getFilename = (path: string): string => {
  return isFolder(path) ?
    path.replace(/\/$/, '').split('/').pop() + '/' :
    path.split('/').pop();
}
export const getParent = (path: string): string => {
  const fullName = isFolder(path) ? path.replace(/\/$/, '') : path
  const parts = fullName.split('/')
  parts.pop()
  return parts.join('/')
}

// a wrapper to get filepaths in the remote storage bucket
export const StorageFilenames = (options: IStorageClientOptions): IStorage_Filenames => {
  const bucketName = STORAGE_BUCKET_NAMES[options.type]
  const prefix = getPrefix()
  const getPath = (filepath: string) => [prefix, filepath].filter(p => p).join('/').replace(/\/+/, '/')
  const getUrl = (filepath: string): string => {
    return options.type == STORAGE_TYPE_PUBLIC ?
      `${STORAGE_BUCKET_PUBLIC_URL}/${getPath(filepath)}` :
      ''
  }
  return {
    bucketName,
    prefix,
    getPath,
    getUrl,
  }
}

export const StorageFile = (file: IField_Storage_File, access_token?: string) => {
  return {
    file,
    isFolder: () => isFolder(file.pointer.path),
    isImage: () => isImageExtension(file.pointer.path),
    bucketName: () => getPointerBucketName(file.pointer),
    path: () => getPointerPath(file.pointer),
    fullPath: () => [STORAGE_BUCKET_PREFIX, getPointerPath(file.pointer)].join('/'),
    storageType: () => getPointerStorageType(file.pointer),
    displayName: () => getFilename(file.pointer.path),
    extension: () => getFileExtension(file.pointer.path),
    mediaLink: () => file.meta.mediaLink || '',
    mediaLinkUrl: () => {
      const url = file.meta.mediaLink || ''
      if(!url) return ''
      return constructUrl(
        getFilestoreMediaUrl(file.pointer.project),
        {
          access_token,
          url,
        }
      )
    },
    url: ({
      onlyPublic = false,
      breakCache = false,
    }: {
      onlyPublic?: boolean,
      breakCache?: boolean,
    } = {}) => {
      const query: Record<string, any> = {}
      if(breakCache) query.timestamp = new Date().getTime()

      const oldFile = file as any

      // the handler for old file types
      if(oldFile.path && oldFile.storageType == STORAGE_TYPE_PUBLIC) {
        const filenames = StorageFilenames({
          type: STORAGE_TYPE_PUBLIC,
        })

        return filenames.getUrl(oldFile.path)
      }

      if(getPointerStorageType(file.pointer) == STORAGE_TYPE_PUBLIC) {
        const filenames = StorageFilenames({
          type: getPointerStorageType(file.pointer),
        })
        const url = file.url ?
          file.url :
          filenames.getUrl(getPointerPath(file.pointer))
        return constructUrl(url, query)
      }
      else {
        query.access_token = access_token
        query.path = file.pointer.path
        const url = onlyPublic ?
          '' :
          getFilestoreDownloadUrl(file.pointer.project, file.pointer.org, file.pointer.type)
        if(!url) return url
        return constructUrl(url, query)
      }
    }
  }
}

export const AppStoragePointer = (
  type: IStorageLocationName,
  project: string,
  org: string,
): IField_Storage_Pointer => {
  const location = STORAGE_LOCATIONS[type]
  if(!location) throw new Error(`no storage location found ${type}`)
  const pointer: IField_Storage_Pointer = Object.assign({}, location, {
    project,
    org,
  })
  return pointer
}

export const TimestampedFile = (
  type: IStorageLocationName,
  project: string,
  org: string,
  ext: string,
  mimeType: string,
  filename?: string,
): IField_Storage_File => {
  const location = STORAGE_LOCATIONS[type]
  if(!location) throw new Error(`no storage location found ${type}`)

  if(!filename) {
    filename = `${new Date().getTime()}.${ext}`
  }
  else {
    filename = `${new Date().getTime()}.${filename}`
  }
  
  const pointer: IField_Storage_Pointer = Object.assign({}, location, {
    project,
    org,
    path: [location.path, filename].join('/'),
  })
  const file: IField_Storage_File = {
    pointer,
    meta: {
      filename,
      type: mimeType,
    }
  }
  const storageFile = StorageFile(file)
  file.url = storageFile.url()
  return file
}

